/**
 * 비밀번호 칸에서 Caps Lock 이 켜져 있으면 칸 바로 아래에 말풍선을 띄운다.
 *
 * <p>비밀번호 칸이 18개 파일에 흩어져 있어 칸마다 붙이지 않고 문서 전체에 한 번 건다 —
 * 새 비밀번호 칸도 따로 손대지 않아도 따라온다. 키를 눌러야 상태를 알 수 있으므로
 * (브라우저가 포커스만으로는 알려 주지 않는다) 첫 키 입력부터 뜬다.
 */

const HINT_TEXT = 'Caps Lock이 켜져 있습니다';

let tip: HTMLDivElement | null = null;
let anchor: HTMLInputElement | null = null;

function isPasswordInput(el: EventTarget | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement && el.type === 'password';
}

function ensureTip(): HTMLDivElement {
  if (tip) return tip;
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  Object.assign(el.style, {
    position: 'fixed',
    zIndex: '2147483647',
    display: 'none',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 12px 7px 8px',
    borderRadius: '10px',
    background: '#fffaf0',
    border: '1px solid #f6d58e',
    color: '#8a5300',
    fontSize: '12.5px',
    fontWeight: '600',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 6px 18px rgba(138, 83, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.06)',
    opacity: '0',
    transform: 'translateY(-4px)',
    transition: 'opacity 140ms ease, transform 140ms ease',
  } satisfies Partial<CSSStyleDeclaration>);

  // 칸을 가리키는 꼬리 — 테두리까지 이어 보이게 같은 색의 마름모를 반쯤 걸친다.
  const arrow = document.createElement('span');
  Object.assign(arrow.style, {
    position: 'absolute',
    top: '-5px',
    left: '16px',
    width: '8px',
    height: '8px',
    background: '#fffaf0',
    borderLeft: '1px solid #f6d58e',
    borderTop: '1px solid #f6d58e',
    transform: 'rotate(45deg)',
  } satisfies Partial<CSSStyleDeclaration>);

  const icon = document.createElement('span');
  icon.textContent = '⇪';
  Object.assign(icon.style, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '6px',
    background: '#f59e0b',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '700',
  } satisfies Partial<CSSStyleDeclaration>);

  const label = document.createElement('span');
  label.textContent = HINT_TEXT;

  el.append(arrow, icon, label);
  document.body.appendChild(el);
  tip = el;
  return el;
}

function place(): void {
  if (!tip || !anchor) return;
  const rect = anchor.getBoundingClientRect();
  tip.style.left = `${Math.round(rect.left)}px`;
  tip.style.top = `${Math.round(rect.bottom + 8)}px`;
}

function show(input: HTMLInputElement): void {
  const wasHidden = anchor === null;
  anchor = input;
  const el = ensureTip();
  el.style.display = 'flex';
  place();
  if (wasHidden) {
    // display 를 바꾼 같은 프레임에 값을 바꾸면 전환이 안 걸린다 — 한 프레임 뒤에 연다.
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }
}

function hide(): void {
  anchor = null;
  if (!tip) return;
  tip.style.display = 'none';
  tip.style.opacity = '0';
  tip.style.transform = 'translateY(-4px)';
}

function onKey(event: KeyboardEvent): void {
  if (!isPasswordInput(event.target)) return;
  if (typeof event.getModifierState !== 'function') return;
  if (event.getModifierState('CapsLock')) show(event.target);
  else hide();
}

let installed = false;

export function initCapsLockHint(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', onKey, true);
  document.addEventListener('keyup', onKey, true);
  document.addEventListener(
    'focusout',
    (event) => {
      if (event.target === anchor) hide();
    },
    true,
  );
  // 모달 안의 칸은 스크롤·창 크기에 따라 움직이므로 붙어 있는 동안 자리를 다시 잡는다.
  window.addEventListener('scroll', place, true);
  window.addEventListener('resize', place);
}
